import { useCallback, useRef } from 'react';

const MOVE_TOLERANCE_PX = 10;

// Touch/pen long-press (~0.5 s) that is cancelled by scrolling or lifting early. Mouse is ignored.
export function useLongPress(onLongPress, { ms = 500 } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    startRef.current = null;
  }, []);

  const onPointerDown = useCallback((e) => {
    if (e.pointerType === 'mouse') return;
    firedRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      navigator.vibrate?.(15);
      onLongPress(e);
    }, ms);
  }, [onLongPress, ms]);

  const onPointerMove = useCallback((e) => {
    if (!startRef.current) return;
    if (Math.hypot(e.clientX - startRef.current.x, e.clientY - startRef.current.y) > MOVE_TOLERANCE_PX) clear();
  }, [clear]);

  // Swallow the click/context menu that follows a completed long-press.
  const onClickCapture = useCallback((e) => {
    if (firedRef.current) { e.preventDefault(); e.stopPropagation(); firedRef.current = false; }
  }, []);
  const onContextMenu = useCallback((e) => {
    if (e.nativeEvent?.pointerType === 'touch' || firedRef.current || startRef.current) e.preventDefault();
  }, []);

  return {
    onPointerDown, onPointerMove, onPointerUp: clear, onPointerCancel: clear, onPointerLeave: clear, onClickCapture, onContextMenu,
  };
}
